import PropTypes from 'prop-types';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Avatar,
    IconButton,
    Grid,
    Paper,
    Divider,
    Chip,
    Button,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Close, Email, Phone, LocationOn, CalendarToday, AssignmentInd, VerifiedUser, School, Badge } from '@mui/icons-material';
import moment from 'moment';
import { motion } from 'framer-motion';
import { getRoleColor } from '../utils/dashboardUtils';
import { BgvChip, TrainingChip } from './StatusChips';

const InfoItem = ({ icon: Icon, label, value, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
        <Box
            sx={{
                width: 42,
                height: 42,
                borderRadius: '12px',
                background: color ? `${color}15` : 'rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}
        >
            <Icon sx={{ color: color || 'text.secondary', fontSize: 20 }} />
        </Box>
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    display: 'block',
                    mb: 0.2,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.65rem'
                }}
            >
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ letterSpacing: '0.02em' }}>
                {value || 'NA'}
            </Typography>
        </Box>
    </Box>
);

InfoItem.propTypes = {
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    color: PropTypes.string
};

const EmployeeDetailsModal = ({ open, handleClose, employee }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const roleColor = employee ? getRoleColor(employee.role) : '#1976d2';

    if (!employee) return null;

    const handleEdit = () => {
        handleClose();
        navigate('/HR/add-sales-employee', { state: { editEmployeeData: employee } });
    };

    const isDeactivated = employee.status !== 'Active';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            fullScreen={fullScreen}
            PaperProps={{
                sx: {
                    borderRadius: fullScreen ? 0 : 6,
                    overflow: 'hidden',
                    maxHeight: '90vh',
                    bgcolor: 'rgba(255, 255, 255, 0.75)',
                    backdropFilter: 'blur(24px)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
                }
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        backdropFilter: 'blur(8px)'
                    }
                }
            }}
        >
            {/* Header Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #1976d2 60%, #42a5f5 100%)',
                    px: 3,
                    py: 2.5,
                    position: 'relative',
                    color: '#fff',
                    overflow: 'hidden'
                }}
            >
                {/* Decorative circles */}
                {[
                    { size: 100, top: -40, right: -20, op: 0.1 },
                    { size: 60, top: 20, right: 60, op: 0.05 }
                ].map((c, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            top: c.top,
                            right: c.right,
                            width: c.size,
                            height: c.size,
                            borderRadius: '50%',
                            bgcolor: '#fff',
                            opacity: c.op,
                            pointerEvents: 'none'
                        }}
                    />
                ))}

                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        color: 'rgba(255,255,255,0.7)',
                        zIndex: 2,
                        '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
                    }}
                >
                    <Close fontSize="small" />
                </IconButton>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}>
                        <Avatar
                            sx={{
                                width: 64,
                                height: 64,
                                fontSize: 28,
                                fontWeight: 800,
                                bgcolor: '#fff',
                                color: roleColor,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                border: '2px solid rgba(255,255,255,0.2)'
                            }}
                        >
                            {employee.name.charAt(0)}
                        </Avatar>
                    </motion.div>

                    <Box>
                        <motion.div
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.1, duration: 0.4 }}
                        >
                            <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.01em', color: '#fff' }}>
                                {employee.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#fff',
                                        opacity: 0.85,
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        fontSize: '0.65rem'
                                    }}
                                >
                                    {employee.role?.replace('_', ' ')}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 600, fontSize: '0.65rem', color: '#fff' }}>
                                    •
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ opacity: 0.7, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.02em', color: '#fff' }}
                                >
                                    ID: {employee.id}
                                </Typography>
                            </Box>
                        </motion.div>
                    </Box>
                </Box>
            </Box>

            <DialogContent sx={{ p: 4, bgcolor: 'transparent' }}>
                <Grid container spacing={4}>
                    {/* Left Column: Personal Info */}
                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2, duration: 0.4 }}
                        >
                            <Box sx={{ mb: 3 }}>
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    color="primary"
                                    sx={{ mb: 2.5, textTransform: 'uppercase', letterSpacing: '0.1em' }}
                                >
                                    Personal Details
                                </Typography>
                                <InfoItem icon={Email} label="Email Address" value={employee.email} color={roleColor} />
                                <InfoItem icon={Phone} label="Mobile Number" value={employee.phone} color={roleColor} />
                            </Box>
                        </motion.div>
                    </Grid>

                    {/* Right Column: Work Info */}
                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3, duration: 0.4 }}
                        >
                            <Box sx={{ mb: 3 }}>
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    color="primary"
                                    sx={{ mb: 2.5, textTransform: 'uppercase', letterSpacing: '0.1em' }}
                                >
                                    Work Information
                                </Typography>
                                <InfoItem icon={AssignmentInd} label="PAN Number" value={employee.pan} color={roleColor} />
                                <InfoItem icon={LocationOn} label="State / Region" value={employee.stateList} color={roleColor} />
                                <InfoItem
                                    icon={CalendarToday}
                                    label="Joining Date"
                                    value={employee.date_of_joining ? moment(employee.date_of_joining).format('DD/MM/YYYY') : 'NA'}
                                    color={roleColor}
                                />
                            </Box>
                        </motion.div>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    {/* Status Highlights */}
                    <Grid item xs={12}>
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.4 }}
                        >
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={4}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 4,
                                            border: '1px solid rgba(255, 255, 255, 0.7)',
                                            bgcolor: 'rgba(255, 255, 255, 0.4)',
                                            backdropFilter: 'blur(12px)',
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                            <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'success.50', display: 'flex' }}>
                                                <VerifiedUser sx={{ color: 'success.main', fontSize: 18 }} />
                                            </Box>
                                            <Typography
                                                variant="caption"
                                                fontWeight={800}
                                                color="text.secondary"
                                                sx={{ letterSpacing: '0.05em' }}
                                            >
                                                BGV STATUS
                                            </Typography>
                                        </Box>
                                        <BgvChip v={employee.bgv_status} />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 4,
                                            border: '1px solid rgba(255, 255, 255, 0.7)',
                                            bgcolor: 'rgba(255, 255, 255, 0.4)',
                                            backdropFilter: 'blur(12px)',
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                            <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'info.50', display: 'flex' }}>
                                                <School sx={{ color: 'info.main', fontSize: 18 }} />
                                            </Box>
                                            <Typography
                                                variant="caption"
                                                fontWeight={800}
                                                color="text.secondary"
                                                sx={{ letterSpacing: '0.05em' }}
                                            >
                                                TRAINING
                                            </Typography>
                                        </Box>
                                        <TrainingChip v={employee.training_status} />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 4,
                                            border: '1px solid rgba(255, 255, 255, 0.7)',
                                            bgcolor: 'rgba(255, 255, 255, 0.4)',
                                            backdropFilter: 'blur(12px)',
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                            <Box
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 2,
                                                    bgcolor: isDeactivated ? 'error.50' : 'success.50',
                                                    display: 'flex'
                                                }}
                                            >
                                                <Badge sx={{ color: isDeactivated ? 'error.main' : 'success.main', fontSize: 18 }} />
                                            </Box>
                                            <Typography
                                                variant="caption"
                                                fontWeight={800}
                                                color="text.secondary"
                                                sx={{ letterSpacing: '0.05em' }}
                                            >
                                                STATUS
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={employee.status || 'Active'}
                                            size="small"
                                            color={isDeactivated ? 'error' : 'success'}
                                            variant="contained"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: '0.7rem',
                                                borderRadius: 2,
                                                boxShadow: isDeactivated
                                                    ? '0 4px 12px rgba(211, 47, 47, 0.2)'
                                                    : '0 4px 12px rgba(46, 125, 50, 0.2)'
                                            }}
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>
                        </motion.div>
                    </Grid>
                </Grid>
            </DialogContent>

            <Box
                sx={{
                    p: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.6)',
                    backdropFilter: 'blur(10px)',
                    borderTop: '1px solid rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2
                }}
            >
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    color="inherit"
                    sx={{ borderRadius: 4, textTransform: 'none', px: 4, py: 1, fontWeight: 800, borderColor: 'rgba(0,0,0,0.2)' }}
                >
                    Close
                </Button>
                <Button
                    variant="contained"
                    onClick={handleEdit}
                    disabled={isDeactivated}
                    sx={{
                        borderRadius: 4,
                        textTransform: 'none',
                        px: 4,
                        py: 1,
                        fontWeight: 800,
                        bgcolor: roleColor,
                        boxShadow: isDeactivated ? 'none' : `0 8px 24px ${roleColor}66`,
                        '&:hover': {
                            bgcolor: roleColor,
                            opacity: 0.9,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 28px ${roleColor}88`
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    Edit Profile
                </Button>
            </Box>
        </Dialog>
    );
};

EmployeeDetailsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    employee: PropTypes.object
};

export default EmployeeDetailsModal;
